import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import ForceGraph3D from "react-force-graph-3d";
import { CSS2DRenderer, CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { motion } from "framer-motion";
import classes from "./ReadOnlyWeb.module.css"; // ✅ reuse DailyWeb styles for visual consistency
import * as d3 from "d3-force";
import ReadOnlyWebHUD from "./ReadOnlyWebHUD/ReadOnlyWebHUD";

const ReadOnlyWeb = () => {
  const { dateId } = useParams();
  const [graphData, setGraphData] = useState(null);
  const fgRef = useRef();

  // --- Fetch archived data ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const snap = await getDoc(doc(db, "dailyweb_results", dateId));
        if (snap.exists()) {
          setGraphData(snap.data());
        } else {
          console.warn("No archived web found for", dateId);
        }
      } catch (err) {
        console.error("Error loading daily web:", err);
      }
    };
    fetchData();
  }, [dateId]);

  // --- Apply gentle forces and camera position ---
  useEffect(() => {
    if (!fgRef.current || !graphData) return;
    fgRef.current.d3Force("charge", d3.forceManyBody().strength(-300));
    fgRef.current.d3Force("link", d3.forceLink().distance(200));
    fgRef.current.d3Force("center", d3.forceCenter(0, 0, 0));
    fgRef.current.cameraPosition({ z: 300 }, 2000);
  }, [graphData]);

  // --- Build HTML node cards identical to DailyWeb ---
  const nodeThreeObject = (node) => {
    const div = document.createElement("div");
    div.className = classes.nodeCard;
    div.style.minWidth = "140px";
    div.style.maxWidth = "240px";
    div.style.padding = "8px 12px";
    div.style.border = "2px solid #ccc";
    div.style.borderRadius = "12px";
    div.style.background = node.answer ? "#e3f2fd" : "white";
    div.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
    div.style.fontSize = "13px";
    div.style.lineHeight = "1.4";
    div.style.pointerEvents = "none";

    const q = document.createElement("div");
    q.innerText = node.question;
    q.style.fontWeight = "600";
    q.style.marginBottom = "6px";
    div.appendChild(q);

    if (node.answer) {
      const a = document.createElement("div");
      a.innerText = `[${node.answeredBy || "Anon"}]: ${node.answer}`;
      a.style.marginTop = "4px";
      a.style.color = "#333";
      div.appendChild(a);
    }

    return new CSS2DObject(div);
  };

  if (!graphData) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: "1rem",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: `
              radial-gradient(circle at 30% 30%, 
                rgba(255,255,255,0.9), 
                rgba(0,112,243,0.8) 40%, 
                rgba(0,0,0,0.25) 100%)`,
          }}
        />
        <p style={{ fontSize: "1rem", color: "#555", fontWeight: 500 }}>
          Loading archived web...
        </p>
      </div>
    );
  }

  return (
    <div className={classes.ideaMapContainer}>

      {/* --- Static 3D graph viewer --- */}
      <ForceGraph3D
        ref={fgRef}
        graphData={graphData}
        nodeThreeObject={nodeThreeObject}
        linkWidth={1}
        linkColor={() => "#999"}
        backgroundColor="#f8f9fa"
        nodeRelSize={6}
        rendererConfig={{ antialias: true }}
        extraRenderers={[new CSS2DRenderer()]}
        cooldownTicks={60}
      />

      <ReadOnlyWebHUD
        dateId={dateId}
        stats={{
        nodes: graphData?.nodes?.length,
        links: graphData?.links?.length,
        }}
        question={graphData?.nodes?.[0]?.question || "Untitled Question"}
    />

      {/* --- Floating summary --- */}
      <motion.div
        className={classes.floatingInfo}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h2>{graphData.nodes?.[0]?.question || "Untitled Question"}</h2>
        <p>
          {graphData.nodes?.length || 0} ideas • {graphData.links?.length || 0} connections
        </p>
      </motion.div>
    </div>
  );
};

export default ReadOnlyWeb;
